import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Lock, Eye, EyeOff, Check, AlertTriangle, X, KeyRound, ShieldCheck, Lightbulb, IdCard } from 'lucide-react'
import HabitPanel, { type Habit as HabitDef } from '@/components/tools/HabitPanel'
import {
  usePasswordCheckCopy,
  type HabitId,
  type PasswordCheckCopy,
  type ReasonKey,
} from '@/components/tools/passwordCheck.copy'
import { assessStrength, preloadStrength, examplePassphrase, type Strength } from '@/lib/password-strength'

export const Route = createFileRoute('/tools/password-check')({
  component: PasswordCheckerPage,
})

/**
 * Two independent questions about one password, both answered in the
 * browser: how strong is it (zxcvbn, local) and has it already leaked
 * (Have I Been Pwned, k-anonymity). One "Check password" runs both.
 *
 * Styling reproduces Lee's HTML prototype (2026-08-18) measure for
 * measure: 18px card radius, shield-shaped icons via clip-path, the two
 * tinted result cards, the "What this means" strip, four habit tiles, and
 * the rule banner with a divider. Every blue in the prototype is violet
 * here; navy is the GranGuard hero dark (#221a3d).
 *
 * Every user-facing string lives in passwordCheck.copy.ts (English and
 * Arabic); this file only holds layout and logic.
 */

type Breach = 'idle' | 'checking' | { count: number } | 'clean' | 'error'

async function sha1Hex(value: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(value))
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()
}

/** Only the first 5 characters of the hash ever leave the browser. */
async function checkBreach(pw: string): Promise<Breach> {
  try {
    const hash = await sha1Hex(pw)
    const prefix = hash.slice(0, 5)
    const suffix = hash.slice(5)
    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      headers: { 'Add-Padding': 'true' },
    })
    if (!res.ok) return 'error'
    const line = (await res.text())
      .split('\n')
      .map(l => l.trim().split(':'))
      .find(([s]) => s === suffix)
    return line ? { count: parseInt(line[1] ?? '0', 10) } : 'clean'
  } catch {
    return 'error'
  }
}

/** Prototype's shield outline. */
const SHIELD = '[clip-path:polygon(50%_0,94%_14%,90%_66%,50%_100%,10%_66%,6%_14%)]'
/**
 * The shield tapers to a point, so its optical centre sits above its
 * geometric centre. Lift the icon so it lands in the wide part rather than
 * the neck: 6px on the 64/72px shields, 9px on the 82/100px ones. (A %
 * translate would be relative to the icon, not the shield.)
 */
const SHIELD_ICON = '-translate-y-[6px] sm:-translate-y-[9px]'
const NAVY = 'text-[#221a3d]'

/** The icon half of each habit tile; the text half comes from the copy module. */
const HABIT_ICONS: Record<HabitId, Pick<HabitDef, 'Icon' | 'iconBg'>> = {
  reuse: { Icon: KeyRound, iconBg: 'bg-violet-50 text-violet-700' },
  personal: { Icon: IdCard, iconBg: 'bg-violet-50 text-violet-700' },
  manager: { Icon: Lock, iconBg: 'bg-violet-50 text-violet-700' },
  '2fa': { Icon: ShieldCheck, iconBg: 'bg-violet-50 text-violet-700' },
}

function PasswordCheckerPage() {
  const c = usePasswordCheckCopy()
  const [pw, setPw] = useState('')
  const [show, setShow] = useState(false)
  const [strength, setStrength] = useState<Strength | null>(null)
  const [breach, setBreach] = useState<Breach>('idle')
  const [busy, setBusy] = useState(false)
  const [example, setExample] = useState('')
  const [openHabit, setOpenHabit] = useState<HabitId | null>(null)
  const checked = useRef('')

  const habits = useMemo<HabitDef[]>(() => c.habits.map(h => ({ ...h, ...HABIT_ICONS[h.id] })), [c])

  useEffect(() => {
    preloadStrength()
    void examplePassphrase().then(setExample)
  }, [])

  useEffect(() => {
    if (pw !== checked.current) {
      setStrength(null)
      setBreach('idle')
    }
  }, [pw])

  async function runCheck() {
    if (!pw || busy) return
    const target = pw
    checked.current = target
    setBusy(true)
    setBreach('checking')
    void assessStrength(target).then(s => {
      if (checked.current === target) setStrength(s)
    })
    const b = await checkBreach(target)
    if (checked.current === target) setBreach(b)
    setBusy(false)
  }

  return (
    <div className="bg-gradient-to-b from-white to-[#fcfbff]">
      <main className="mx-auto max-w-[1240px] px-4 pb-[38px] pt-[22px] sm:px-7 sm:pb-[54px] sm:pt-[46px]">
        {/* Hero */}
        <section className="mb-7 grid grid-cols-1 items-center gap-4 sm:grid-cols-[72px_1fr] lg:grid-cols-[110px_1fr] lg:gap-[26px]">
          <div
            aria-hidden
            className={`hidden h-[72px] w-[64px] place-items-center bg-gradient-to-br from-violet-500 to-violet-800 text-white sm:grid lg:h-[100px] lg:w-[90px] ${SHIELD}`}
          >
            <Lock className={`h-7 w-7 lg:h-9 lg:w-9 ${SHIELD_ICON}`} strokeWidth={2.25} />
          </div>
          <div>
            <h1 className={`m-0 mb-[10px] text-[36px] font-bold leading-[1.02] tracking-[-1.4px] sm:text-[40px] lg:text-[56px] lg:tracking-[-2.2px] ${NAVY}`}>
              {c.title}
            </h1>
            <p className="m-0 text-[16px] text-[#2f2a4a] sm:text-[18px]">{c.intro}</p>
            <div className="mt-[14px] flex items-center gap-2 text-[15px] font-bold text-violet-700">
              <Lock className="h-4 w-4" strokeWidth={2.5} />
              <span>{c.assurance}</span>
            </div>
          </div>
        </section>

        {/* Checker card */}
        <section
          aria-label={c.checkerAria}
          className="mb-5 grid grid-cols-1 gap-[18px] rounded-[18px] border border-[#e6e2f3] bg-white p-5 shadow-[0_10px_28px_rgba(56,23,112,.07)] lg:grid-cols-[1fr_260px]"
        >
          <div className="flex h-[72px] items-center rounded-[13px] border-[1.5px] border-[#b9a9d8] bg-white ps-5 pe-[14px] focus-within:border-violet-600 focus-within:shadow-[0_0_0_4px_rgba(124,58,237,.08)]">
            <label htmlFor="pw" className="sr-only">{c.inputLabel}</label>
            <input
              id="pw"
              type={show ? 'text' : 'password'}
              value={pw}
              onChange={e => setPw(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') runCheck()
              }}
              placeholder={c.placeholder}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              className="w-full border-0 bg-transparent text-[22px] tracking-[.06em] text-[#1c1131] outline-none placeholder:tracking-normal placeholder:text-slate-400"
            />
            <button
              type="button"
              onClick={() => setShow(v => !v)}
              aria-label={show ? c.hidePassword : c.showPassword}
              title={show ? c.hidePassword : c.showPassword}
              className={`cursor-pointer border-0 bg-transparent p-[10px] ${NAVY}`}
            >
              {show ? <EyeOff className="h-6 w-6" /> : <Eye className="h-6 w-6" />}
            </button>
          </div>
          <button
            type="button"
            onClick={runCheck}
            disabled={!pw || busy}
            className="h-[60px] cursor-pointer rounded-[12px] border-0 bg-gradient-to-b from-violet-600 to-violet-800 text-[18px] font-extrabold text-white shadow-[0_8px_18px_rgba(124,58,237,.2)] transition-transform hover:-translate-y-px active:translate-y-0 disabled:cursor-not-allowed disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 disabled:shadow-none lg:h-auto"
          >
            {busy ? c.checking : c.check}
          </button>
        </section>

        {strength && (
          <>
            {/* Results */}
            <section aria-live="polite" className="mb-[18px] grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-5">
              <StrengthCard strength={strength} copy={c} />
              <BreachCard breach={breach} copy={c} />
            </section>

            {/* What this means */}
            <section className="mb-6 grid grid-cols-1 items-center gap-5 rounded-[18px] border border-violet-200 bg-[#faf8ff] px-5 py-5 sm:px-7 sm:py-[22px] lg:grid-cols-[250px_1fr]">
              <div className={`flex items-center gap-[18px] text-[22px] font-extrabold ${NAVY}`}>
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-violet-600 font-serif text-[34px] font-bold text-white">
                  i
                </span>
                <span>{c.meaningTitle}</span>
              </div>
              <ul className="m-0 list-disc ps-6 text-[17px] leading-[1.9] text-[#2c2a45]">
                {meaning(strength, breach, c).map(line => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </section>
          </>
        )}

        {/* Habits. Each tile opens a slide-over with the why and the how. */}
        <h2 className={`m-0 mb-[13px] text-[21px] font-bold ${NAVY}`}>{c.habitsTitle}</h2>
        <section className="mb-[22px] grid grid-cols-1 gap-[14px] sm:grid-cols-2 lg:grid-cols-4">
          {habits.map(h => (
            <HabitTile key={h.id} habit={h} learnMore={c.learnMore} onOpen={() => setOpenHabit(h.id)} />
          ))}
        </section>
        <HabitPanel habit={habits.find(h => h.id === openHabit) ?? null} onClose={() => setOpenHabit(null)} />

        {/* Rule */}
        <section className="grid grid-cols-1 items-center gap-4 rounded-[18px] border border-violet-200 bg-[#f9f7ff] px-5 py-5 sm:px-7 sm:py-[22px] lg:grid-cols-[1fr_1px_auto] lg:gap-8">
          <div className="flex items-center gap-5">
            <div className="grid h-[58px] w-[58px] min-w-[58px] place-items-center rounded-full bg-gradient-to-b from-violet-400 to-violet-600 text-white">
              <Lightbulb className="h-7 w-7" strokeWidth={2.25} />
            </div>
            <div>
              <div className="mb-1 text-[15px] font-semibold text-violet-700">{c.ruleEyebrow}</div>
              <div className={`text-[21px] font-semibold tracking-[-.3px] sm:text-[24px] ${NAVY}`}>{c.ruleText}</div>
            </div>
          </div>
          <div aria-hidden className="hidden h-[66px] bg-violet-200 lg:block" />
          {/* `auto` column + nowrap: the box grows to fit the passphrase
              instead of the passphrase wrapping to fit the box. */}
          <div className="min-w-0 lg:min-w-[360px]">
            <div className="mb-2 text-[14px] font-semibold text-violet-700">{c.tryPassphrase}</div>
            <div className="flex items-center gap-3">
              <div className="flex-1 whitespace-nowrap rounded-[10px] border border-violet-300 bg-white px-4 py-3 text-center font-mono text-[17px] text-violet-900">
                {example || '…'}
              </div>
              <button
                type="button"
                onClick={() => void examplePassphrase().then(setExample)}
                className="shrink-0 cursor-pointer border-0 bg-transparent text-[14px] font-semibold text-violet-700 hover:text-violet-900"
              >
                {c.another}
              </button>
            </div>
          </div>
        </section>

        <p className="mx-auto mt-4 max-w-[820px] text-center text-[13px] leading-[1.55] text-[#7a7492]">{c.footer}</p>
      </main>
    </div>
  )
}

/* ── Cards ────────────────────────────────────────────────────────── */

/**
 * The result icon: a plain circle with one of three glyphs. Good is a check,
 * caution is a triangle, bad is an X. Same vocabulary on both cards.
 */
function ResultIcon({ state }: { state: 'good' | 'caution' | 'bad' | 'neutral' }) {
  const bg =
    state === 'good' ? 'bg-[#278a2f]'
    : state === 'caution' ? 'bg-[#d08a12]'
    : state === 'bad' ? 'bg-[#c9382a]'
    : 'bg-slate-300'
  return (
    <div className={`grid h-16 w-16 place-items-center rounded-full text-white sm:h-[76px] sm:w-[76px] ${bg}`}>
      {state === 'good' && <Check className="h-8 w-8 sm:h-10 sm:w-10" strokeWidth={3} />}
      {state === 'caution' && <AlertTriangle className="h-8 w-8 sm:h-9 sm:w-9" strokeWidth={2.5} />}
      {state === 'bad' && <X className="h-8 w-8 sm:h-10 sm:w-10" strokeWidth={3} />}
      {state === 'neutral' && <span className="h-2 w-8 rounded-full bg-white/80" />}
    </div>
  )
}

/**
 * Strength.score is derived from the crack time we show and capped by
 * zxcvbn (see password-strength.ts), so the score word always agrees with
 * the number beside it: "Very strong" only ever appears with "centuries".
 */
function StrengthCard({ strength, copy: c }: { strength: Strength; copy: PasswordCheckCopy }) {
  const state = strength.score >= 3 ? 'good' : strength.score === 2 ? 'caution' : 'bad'
  const card =
    state === 'good' ? 'bg-[#f5fbf4] border-[#cfe8ca]'
    : state === 'caution' ? 'bg-[#fffaf0] border-[#f1dfb0]'
    : 'bg-[#fff5f4] border-[#f0c4bf]'
  const badge =
    state === 'good' ? 'bg-[#e9f6e7] border-[#bdddb7] text-[#236729]'
    : state === 'caution' ? 'bg-[#fff4dc] border-[#ecd39a] text-[#7a5410]'
    : 'bg-[#fff0ee] border-[#efbbb4] text-[#9a3026]'
  const num = state === 'good' ? 'text-[#278a2f]' : state === 'caution' ? 'text-[#a3670c]' : 'text-[#b3352a]'

  return (
    <article className={`grid min-h-[164px] grid-cols-[64px_1fr] items-center gap-[18px] rounded-[18px] border px-[18px] py-[22px] sm:grid-cols-[92px_1fr] sm:px-[30px] sm:py-[26px] ${card}`}>
      <ResultIcon state={state} />
      <div>
        <h2 className={`m-0 mb-[10px] text-[23px] font-bold ${NAVY}`}>{c.strengthTitle}</h2>
        <div className={`mb-[10px] inline-flex rounded-[10px] border px-[13px] py-[7px] text-[15px] font-extrabold ${badge}`}>
          {c.scoreWord[strength.score]}
        </div>
        <p className="m-0 text-[17px] text-[#2c2a45]">
          {c.crackTimeLabel}
          <strong className={`text-[20px] ${num}`}>{c.crackTime(strength)}.</strong>
        </p>
      </div>
    </article>
  )
}

function BreachCard({ breach, copy: c }: { breach: Breach; copy: PasswordCheckCopy }) {
  const leaked = typeof breach === 'object'
  const clean = breach === 'clean'
  const card = leaked ? 'bg-[#fff5f4] border-[#f0c4bf]' : clean ? 'bg-[#f5fbf4] border-[#cfe8ca]' : 'bg-[#f7f4ff] border-violet-200'
  const state = leaked ? 'bad' : clean ? 'good' : 'neutral'
  const badge = leaked
    ? 'bg-[#fff0ee] border-[#efbbb4] text-[#9a3026]'
    : clean
      ? 'bg-[#e9f6e7] border-[#bdddb7] text-[#236729]'
      : 'bg-slate-100 border-slate-200 text-slate-600'
  const label =
    breach === 'checking' ? c.breachChecking
    : leaked ? c.breachFound
    : clean ? c.breachNotFound
    : breach === 'error' ? c.breachError
    : c.breachIdle
  const text =
    leaked ? (
      <>
        {c.breachLeakedBefore}
        <strong className="text-[20px] text-[#b3352a]">{c.breachLeakedCount(breach.count)}</strong>
        {c.breachLeakedAfter}
      </>
    ) : clean ? (
      c.breachCleanText
    ) : breach === 'error' ? (
      c.breachErrorText
    ) : (
      c.breachIdleText
    )

  return (
    <article className={`grid min-h-[164px] grid-cols-[64px_1fr] items-center gap-[18px] rounded-[18px] border px-[18px] py-[22px] sm:grid-cols-[92px_1fr] sm:px-[30px] sm:py-[26px] ${card}`}>
      <ResultIcon state={state} />
      <div>
        <h2 className={`m-0 mb-[10px] text-[23px] font-bold ${NAVY}`}>{c.breachTitle}</h2>
        <div className={`mb-[10px] inline-flex rounded-[10px] border px-[13px] py-[7px] text-[15px] font-extrabold ${badge}`}>
          {label}
        </div>
        <p className="m-0 text-[17px] text-[#2c2a45]">{text}</p>
      </div>
    </article>
  )
}

function HabitTile({ habit, learnMore, onOpen }: { habit: HabitDef; learnMore: string; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="grid cursor-pointer grid-cols-[54px_1fr] items-start gap-[14px] rounded-[15px] border border-[#e6e2f3] bg-white p-5 text-start transition-shadow hover:border-violet-300 hover:shadow-[0_10px_28px_rgba(56,23,112,.07)] focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
    >
      <div className={`grid h-12 w-12 place-items-center rounded-full ${habit.iconBg}`}>
        <habit.Icon className="h-6 w-6" strokeWidth={2.25} />
      </div>
      <div>
        <h3 className={`mb-[6px] mt-px text-[16px] font-bold leading-[1.25] ${NAVY}`}>{habit.title}</h3>
        <p className="m-0 text-[14px] leading-[1.45] text-[#5c5a75]">{habit.short}</p>
        <p className="mt-2 text-[13px] font-bold text-violet-700">{learnMore}</p>
      </div>
    </button>
  )
}

/**
 * Three plain bullets that put both answers together. Written for someone
 * who has never thought about how passwords get broken, which means
 * concrete rather than abstract: a criminal, a computer, a number of
 * minutes. The crack time on the card above is the fact; these bullets say
 * what it means.
 */
function meaning(st: Strength, br: Breach, c: PasswordCheckCopy): string[] {
  const out: string[] = []
  const time = c.crackTime(st)

  // 1. What the strength number means in practice.
  if (st.score >= 3) {
    out.push(c.meaningWouldNeed(st.score >= 4 ? c.centuries : c.about(time)))
  } else {
    out.push(c.meaningWouldCrack(st.score === 0 ? time : c.about(time)))
  }

  // 2. Why, in plain words, when the estimator found a reason.
  const why = plainReason(st)
  if (why) out.push(c.reasons[why])

  // 3. The breach answer, with the number when there is one.
  if (typeof br === 'object') {
    out.push(c.meaningLeaked(br.count))
  } else if (br === 'clean') {
    out.push(c.meaningClean)
  }

  // Reuse is not repeated here: it is one of the four habit tiles directly
  // below, and a line on every result reads as nagging.
  return out
}

/**
 * zxcvbn's feedback is written for developers ("Add more words that are
 * less common") and is always English (the estimator loads the `en`
 * dictionaries). It is never shown; this maps the handful of cases that
 * matter to a key in the copy module, where the sentence a person would say
 * lives in each language. Returns null when there is nothing specific to add.
 */
function plainReason(st: Strength): ReasonKey | null {
  const w = (st.warning ?? '').toLowerCase()
  const sugg = st.suggestions.join(' ').toLowerCase()
  const kinds = new Set(st.parts.map(p => p.kind))

  if (w.includes('heavily used') || w.includes('commonly used') || w.includes('top-10') || w.includes('top-100')) {
    return 'common'
  }
  if (w.includes('similar to a commonly used')) return 'commonTwist'
  if (sugg.includes('substitutions')) return 'substitutions'
  if (w.includes('dates') || w.includes('years') || kinds.has('date')) return 'date'
  if (w.includes('keyboard') || kinds.has('keyboard-walk')) return 'keyboard'
  if (w.includes('repeat') || kinds.has('repeat')) return 'repeat'
  if (kinds.has('name')) return 'name'
  if (st.score <= 2 && kinds.has('word')) return 'words'
  if (st.score <= 1) return 'short'
  return null
}
